/**
 * Iter 89.4 / Sprint 2A.3 — DailyDrillDown
 * ─────────────────────────────────────────────────────────────────
 * Modal dialog that shows the 288-slot timeline for one
 * registration on one local-calendar day. Consumes the existing
 * /api/workforce/desktop-activity/timeline endpoint (admin/manager
 * RBAC enforced server-side).
 *
 * Stateful only for: the selected `date` and the loading status of
 * the timeline payload. Reuses <ActivityTimelineStrip> for the
 * rendering.
 *
 * Privacy: only buckets + seconds are displayed — same surface as
 * the ESS variant. No app titles, URLs, or other prohibited
 * categories.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { CircleNotch, CaretLeft, CaretRight, X } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import ActivityTimelineStrip from "./ActivityTimelineStrip";

const MAX_DAYS_BACK = 30;

function todayLocalDateString() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function addDaysToISO(iso, deltaDays) {
    const [y, m, d] = iso.split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() + deltaDays);
    return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
}

function fmtSecs(s) {
    const total = Number(s) || 0;
    if (total === 0) return "0m";
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    if (h === 0) return `${m}m`;
    return `${h}h ${String(m).padStart(2, "0")}m`;
}

export function DailyDrillDown({ open, onClose, row }) {
    const initialDate = useMemo(todayLocalDateString, []);
    const [date, setDate] = useState(initialDate);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const todayISO = useMemo(todayLocalDateString, []);
    const earliestISO = useMemo(() => addDaysToISO(todayISO, -MAX_DAYS_BACK), [todayISO]);
    const canGoPrev = date > earliestISO;
    const canGoNext = date < todayISO;
    const isToday = date === todayISO;

    const load = useCallback(async () => {
        if (!row?.registration_id) return;
        setLoading(true);
        try {
            const resp = await api.get(
                `/workforce/desktop-activity/timeline?registration_id=${encodeURIComponent(row.registration_id)}&date=${encodeURIComponent(date)}`,
            );
            setData(resp.data);
        } catch (err) {
            setData(null);
            const status = err?.response?.status;
            if (status === 404 || status === 403) {
                toast.error(formatApiError(err, "Timeline not available"));
            } else {
                toast.error(formatApiError(err, "Failed to load timeline"));
            }
        } finally {
            setLoading(false);
        }
    }, [row?.registration_id, date]);

    useEffect(() => {
        if (open) {
            Promise.resolve().then(() => { load(); });
        }
    }, [open, load]);

    // Reset state when the modal closes so the next open starts on today.
    useEffect(() => {
        if (!open) {
            setData(null);
            setDate(initialDate);
        }
    }, [open, initialDate]);

    if (!open || !row) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4"
            data-testid="drilldown-backdrop"
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
            <div
                className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto"
                data-testid="drilldown-modal"
            >
                <div className="flex items-start justify-between gap-4 p-6 border-b border-slate-100">
                    <div>
                        <div className="text-[11px] uppercase tracking-wide text-slate-500">Daily timeline</div>
                        <div className="text-lg font-semibold text-slate-900" data-testid="drilldown-employee-name">
                            {row.employee_name || "—"}
                        </div>
                        <div className="text-sm text-slate-500" data-testid="drilldown-machine-name">
                            {row.machine_name || "no machine"} · {row.registration_id}
                        </div>
                    </div>
                    <button
                        type="button"
                        className="p-2 rounded hover:bg-slate-100 text-slate-500"
                        onClick={onClose}
                        aria-label="Close timeline"
                        data-testid="drilldown-close"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="text-sm text-slate-700 font-medium" data-testid="drilldown-date">
                            {date}{isToday ? " · today" : ""}
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={!canGoPrev || loading}
                                onClick={() => setDate((d) => addDaysToISO(d, -1))}
                                className="gap-1.5"
                                data-testid="drilldown-prev-day"
                                aria-label="Previous day"
                            >
                                <CaretLeft size={14} />
                                Previous
                            </Button>
                            <input
                                type="date"
                                value={date}
                                min={earliestISO}
                                max={todayISO}
                                onChange={(e) => {
                                    const next = e.target.value;
                                    if (next) setDate(next);
                                }}
                                className="h-8 px-2 rounded border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200"
                                data-testid="drilldown-date-picker"
                            />
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={!canGoNext || loading}
                                onClick={() => setDate((d) => addDaysToISO(d, 1))}
                                className="gap-1.5"
                                data-testid="drilldown-next-day"
                                aria-label="Next day"
                            >
                                Next
                                <CaretRight size={14} />
                            </Button>
                        </div>
                    </div>

                    {loading ? (
                        <div className="h-9 flex items-center justify-center text-slate-400 text-xs">
                            <CircleNotch size={14} className="animate-spin mr-2" /> Loading timeline…
                        </div>
                    ) : data ? (
                        <>
                            <ActivityTimelineStrip
                                timeline={data.timeline || []}
                                slotMinutes={data.slot_minutes || 5}
                                slotsPerDay={data.slots_per_day || 288}
                                testIdPrefix="drilldown-timeline"
                            />
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4 pt-4 border-t border-slate-100 text-sm">
                                <div data-testid="drilldown-summary-active">
                                    <div className="text-[11px] text-slate-500">Active</div>
                                    <div className="font-mono text-emerald-700">{fmtSecs(data.summary?.active_seconds)}</div>
                                </div>
                                <div data-testid="drilldown-summary-idle">
                                    <div className="text-[11px] text-slate-500">Idle</div>
                                    <div className="font-mono text-amber-700">{fmtSecs(data.summary?.idle_seconds)}</div>
                                </div>
                                <div data-testid="drilldown-summary-locked">
                                    <div className="text-[11px] text-slate-500">Locked</div>
                                    <div className="font-mono text-slate-700">{fmtSecs(data.summary?.locked_seconds)}</div>
                                </div>
                                <div data-testid="drilldown-summary-windows">
                                    <div className="text-[11px] text-slate-500">Windows</div>
                                    <div className="font-mono text-slate-700">{data.summary?.windows_reported ?? 0}</div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="text-slate-400 text-xs">No timeline data.</div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default DailyDrillDown;
